import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import {
  toStorageMaterialOrderBrandName,
  type StorageMaterialOrderCard,
} from '@/utils/approval/storageMaterialOrder';
import { StorageMaterialOrderRoot } from './styles';

const text = (v: unknown) =>
  v === null || v === undefined ? '' : String(v).trim();

const toNumber = (v: unknown) => Number(text(v).replace(/,/g, '')) || 0;

/** 레거시 number_format — 숫자가 아니면 원문 */
const formatNumber = (v: unknown) => {
  const raw = text(v);
  const n = Number(raw.replace(/,/g, ''));
  return raw && Number.isFinite(n)
    ? Math.round(n).toLocaleString('en-US')
    : raw;
};

const DETAIL_COL_WIDTHS = [
  '7%',
  '7%',
  '7%',
  '19%',
  '10%',
  '10%',
  '10%',
  '10%',
  '10%',
  '10%',
];

/** 레거시 appr_sub_view_forms_sub_order_mstr — 라인마다 한 행, 맨 아래 합계 */
const StorageMaterialOrderContentView = ({
  card,
  t,
}: {
  card: StorageMaterialOrderCard;
  t: TFunction;
}) => {
  const lines = card.lines ?? [];
  const totalQty = lines.reduce((sum, l) => sum + toNumber(l.qty), 0);
  const totalAmount = lines.reduce((sum, l) => sum + toNumber(l.orderAmt), 0);
  return (
    <>
      <section>
        <table>
          <colgroup>
            <col width="33%" />
            <col width="34%" />
            <col width="33%" />
          </colgroup>
          <tbody>
            <tr>
              <th className="text-center">{t('브랜드')}</th>
              <th className="text-center">{t('오더번호')}</th>
              <th className="text-center">{t('발주일')}</th>
            </tr>
            <tr>
              <td className="text-center">
                {toStorageMaterialOrderBrandName(card.brand)}
              </td>
              <td className="text-center">{text(card.orderNo)}</td>
              <td className="text-center">{text(card.orderDate)}</td>
            </tr>
          </tbody>
        </table>
      </section>
      {lines.length > 0 && (
        <div>
          <table>
            <colgroup>
              {DETAIL_COL_WIDTHS.map((w, i) => (
                <col key={i} width={w} />
              ))}
            </colgroup>
            <tbody>
              <tr>
                <th className="text-center bg-info" colSpan={10}>
                  {t('발주 상세')}
                </th>
              </tr>
              <tr>
                <th className="text-center" colSpan={8}>
                  {t('현황')}
                </th>
                <th className="text-center" colSpan={2}>
                  {t('발주정보')}
                </th>
              </tr>
              <tr>
                {[
                  t('대분류'),
                  t('중분류'),
                  t('소분류'),
                  t('규격'),
                  t('단가'),
                  t('현재고'),
                  t('재고금액'),
                  t('업체명'),
                  t('발주량'),
                  t('발주금액'),
                ].map((label, i) => (
                  <th key={i} className="text-center">
                    {label}
                  </th>
                ))}
              </tr>
              {lines.map((line, i) => (
                <tr key={i}>
                  <td className="text-center">{text(line.smatClassName)}</td>
                  <td className="text-center">{text(line.smatDivisionName)}</td>
                  <td className="text-center">{text(line.smatStdName)}</td>
                  <td className="text-center">{text(line.smatFormName)}</td>
                  <td className="text-right">{formatNumber(line.unitPrice)}</td>
                  <td className="text-right">
                    {formatNumber(line.stockQuantity)}
                  </td>
                  <td className="text-right">{formatNumber(line.invAmt)}</td>
                  <td className="text-center">{text(line.custName)}</td>
                  <td className="text-right">{formatNumber(line.qty)}</td>
                  <td className="text-right">{formatNumber(line.orderAmt)}</td>
                </tr>
              ))}
              <tr>
                <td className="text-center bg-danger" colSpan={8}>
                  {t('합계')}
                </td>
                <td className="text-right bg-danger textBold">
                  {formatNumber(totalQty)}
                </td>
                <td className="text-right bg-danger">
                  {formatNumber(totalAmount)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

/** attributes.content — 조회 화면은 이 HTML 을 그대로 보여 줌 */
export const buildStorageMaterialOrderContentHtml = (
  card: StorageMaterialOrderCard,
  t: TFunction,
): string =>
  renderToStaticMarkup(<StorageMaterialOrderContentView card={card} t={t} />);

/** 저장 본문 그대로(레거시 문서 포함). 레거시 본문에 같이 저장된 style 은 버림 */
export const StorageMaterialOrderSnapshot = ({
  content,
}: {
  content: string;
}) => (
  <StorageMaterialOrderRoot>
    <SerpContentSnapshot content={content} />
  </StorageMaterialOrderRoot>
);
